import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, GraduationCap, Star } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "",
  full_name: "",
  current_profession: "",
  batch_year: "",
  linkedin_url: "",
  photo_url: "",
  photo_public_id: "",
  order: 0,
  featured: false,
  status: "Active",
};

const FEATURED_MAX = 6;

function Field({ label, children, hint, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-[#6b655b] mt-1.5 leading-snug">{hint}</div>}
    </label>
  );
}

export default function Alumni() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/alumni");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openAdd = () => {
    setForm({ ...emptyForm, order: items.length });
    setOpen(true);
  };

  const openEdit = (a) => {
    setForm({ ...emptyForm, ...a });
    setOpen(true);
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error("Full Name is required");
      return;
    }
    const willFeature = !!form.featured;
    const wasFeatured = !!items.find((x) => x.id === form.id)?.featured;
    if (willFeature && !wasFeatured) {
      const currentFeaturedCount = items.filter((x) => x.featured && x.id !== form.id).length;
      if (currentFeaturedCount >= FEATURED_MAX) {
        toast.error(`Only ${FEATURED_MAX} alumni can be featured on the home page. Unfeature one first.`);
        return;
      }
    }
    const payload = {
      full_name: form.full_name.trim(),
      current_profession: (form.current_profession || "").trim(),
      batch_year: (form.batch_year || "").trim(),
      linkedin_url: (form.linkedin_url || "").trim(),
      photo_url: form.photo_url || "",
      photo_public_id: form.photo_public_id || "",
      order: Number(form.order) || 0,
      featured: willFeature,
      status: form.status === "Inactive" ? "Inactive" : "Active",
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/institute/alumni/${form.id}`, payload);
        toast.success("Alumni updated");
      } else {
        await api.post("/institute/alumni", payload);
        toast.success("Alumni added");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/alumni/${confirmDelete.id}`);
      toast.success("Alumni deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const featuredCount = items.filter((a) => a.featured).length;

  const toggleFeatured = async (a) => {
    const next = !a.featured;
    if (next && featuredCount >= FEATURED_MAX) {
      toast.error(`Only ${FEATURED_MAX} alumni can be featured on the home page. Unfeature one first.`);
      return;
    }
    try {
      await api.put(`/institute/alumni/${a.id}`, {
        full_name: a.full_name,
        current_profession: a.current_profession || "",
        batch_year: a.batch_year || "",
        linkedin_url: a.linkedin_url || "",
        photo_url: a.photo_url || "",
        photo_public_id: a.photo_public_id || "",
        order: Number(a.order) || 0,
        featured: next,
        status: a.status === "Inactive" ? "Inactive" : "Active",
      });
      toast.success(next ? "Featured on home page" : "Removed from home page");
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="i-alumni-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Institute CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Notable Alumni</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">
            Add alumni profiles (Name, Current Profession, Photo · 300 KB) — feature up to {FEATURED_MAX} on the home page.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="i-alumni-featured-counter"
            className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-sm border ${
              featuredCount >= FEATURED_MAX
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-[#f5f1e8] border-[#e5e0d8] text-[#234333]"
            }`}
          >
            <Star size={12} strokeWidth={1.8} className="fill-current" />
            Featured on Home: {featuredCount}/{FEATURED_MAX}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={openAdd}
              data-testid="i-alumni-add-btn"
              className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-colors"
            >
              <Plus size={16} strokeWidth={1.8} /> Add Alumni
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]" data-testid="i-alumni-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div
          data-testid="i-alumni-empty"
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm py-16 text-center"
        >
          <GraduationCap size={36} strokeWidth={1.2} className="mx-auto text-[#bcb6aa] mb-3" />
          <div className="text-[#1c1a17] font-medium">No alumni added yet</div>
          <div className="text-sm text-[#6b655b] mt-1">Click <em>Add Alumni</em> to create one.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="i-alumni-grid">
          {items.map((a) => (
            <div
              key={a.id}
              className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col"
              data-testid={`i-alumni-card-${a.id}`}
            >
              <div className="aspect-square bg-[#f5f1e8] overflow-hidden relative">
                {a.photo_url ? (
                  <img src={a.photo_url} alt={a.full_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#bcb6aa]">
                    <GraduationCap size={36} strokeWidth={1.2} />
                  </div>
                )}
                {a.featured && (
                  <div
                    data-testid={`i-alumni-featured-badge-${a.id}`}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 bg-[#234333] text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold shadow-sm"
                  >
                    <Star size={10} strokeWidth={2} className="fill-current" />
                    On Home
                  </div>
                )}
                {a.status !== "Active" && (
                  <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">
                    Inactive
                  </div>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-semibold text-[#1c1a17] leading-snug text-sm">{a.full_name}</div>
                {a.batch_year && (
                  <div className="text-[10px] tracking-[0.15em] uppercase text-[#6b655b] font-semibold mt-0.5">Batch {a.batch_year}</div>
                )}
                {a.current_profession && (
                  <div className="text-xs text-[#5b5650] mt-1 line-clamp-2">{a.current_profession}</div>
                )}
                {canEdit && (
                  <div className="mt-auto pt-2 flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => toggleFeatured(a)}
                      data-testid={`i-alumni-toggle-featured-${a.id}`}
                      title={a.featured ? "Remove from home page" : "Feature on home page"}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm transition-colors ${
                        a.featured
                          ? "text-[#234333] bg-[#234333]/10 hover:bg-[#234333]/20"
                          : "text-[#6b655b] hover:bg-[#f5f1e8]"
                      }`}
                    >
                      <Star size={11} strokeWidth={1.8} className={a.featured ? "fill-current" : ""} />
                      {a.featured ? "On Home" : "Feature"}
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(a)}
                      data-testid={`i-alumni-edit-${a.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm transition-colors"
                    >
                      <Pencil size={11} strokeWidth={1.6} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(a)}
                      data-testid={`i-alumni-delete-${a.id}`}
                      className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm transition-colors"
                    >
                      <Trash2 size={11} strokeWidth={1.6} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Alumni" : "Add Alumni"}</DialogTitle>
            <DialogDescription>
              Add the alumni&apos;s full name, their current profession and a photo (max 300 KB).
            </DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Full Name *" full>
              <input
                data-testid="i-alumni-input-name"
                className={inputCls}
                placeholder="e.g., Dr. Anjali Joshi"
                value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                maxLength={160}
              />
            </Field>
            <Field label="Current Profession" full hint="e.g., Senior Software Engineer at Google">
              <input
                data-testid="i-alumni-input-profession"
                className={inputCls}
                placeholder="Designation · Company / City"
                value={form.current_profession}
                onChange={(e) => set("current_profession", e.target.value)}
                maxLength={240}
              />
            </Field>
            <Field label="Batch Year" hint="e.g., 2012 or 2010-13">
              <input
                data-testid="i-alumni-input-batch-year"
                className={inputCls}
                placeholder="2012"
                value={form.batch_year}
                onChange={(e) => set("batch_year", e.target.value)}
                maxLength={20}
              />
            </Field>
            <Field label="LinkedIn URL" hint="Full URL — e.g., https://www.linkedin.com/in/anjali-joshi">
              <input
                data-testid="i-alumni-input-linkedin"
                className={inputCls}
                placeholder="https://www.linkedin.com/in/…"
                value={form.linkedin_url}
                onChange={(e) => set("linkedin_url", e.target.value)}
                maxLength={400}
              />
            </Field>
            <Field label="Display Order">
              <input
                data-testid="i-alumni-input-order"
                type="number"
                className={inputCls}
                value={form.order}
                onChange={(e) => set("order", e.target.value)}
              />
            </Field>
            <Field label="Status">
              <select
                data-testid="i-alumni-input-status"
                className={inputCls}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <label className="flex items-start gap-3 p-3 border border-[#e5e0d8] rounded-sm bg-[#fbf8f1] cursor-pointer hover:bg-[#f5f1e8] transition-colors">
                <input
                  type="checkbox"
                  data-testid="i-alumni-input-featured"
                  checked={!!form.featured}
                  onChange={(e) => set("featured", e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#234333]"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-[#1c1a17]">
                    <Star size={13} strokeWidth={1.8} className={form.featured ? "fill-[#234333] text-[#234333]" : "text-[#6b655b]"} />
                    Feature on Home Page
                  </span>
                  <span className="block text-[11px] text-[#6b655b] mt-1 leading-snug">
                    Show this alumni in the “Notable Alumni” strip on the public home page. Max {FEATURED_MAX} per institute · currently {featuredCount}/{FEATURED_MAX} featured.
                  </span>
                </span>
              </label>
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Photo · max 300 KB
              </div>
              <FileUploader
                kind="inst_alumni_photo"
                label="Alumni photo"
                url={form.photo_url}
                onChange={(next) => {
                  set("photo_url", next?.url || "");
                  set("photo_public_id", next?.public_id || "");
                }}
                testid="i-alumni-photo"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-sm border border-[#e5e0d8] hover:bg-[#f5f1e8] transition-colors"
              data-testid="i-alumni-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold rounded-sm bg-[#234333] hover:bg-[#1a3326] text-white transition-colors disabled:opacity-60"
              data-testid="i-alumni-save-btn"
            >
              {saving ? "Saving…" : form.id ? "Update Alumni" : "Add Alumni"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this alumni?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{confirmDelete?.full_name}</span> will be removed from the website. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="i-alumni-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-red-600 hover:bg-red-700"
              data-testid="i-alumni-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
